import { FundingPolicyType } from '../appModel';
import {
  partnershipStateConfig,
  AGE_NO_INFLATION_REQUIRED,
} from './partnershipStateConfig';


function parseInflationPercentFromOption(inflationOption: string | number | null): number {
  if (
    !inflationOption ||
    inflationOption === 'no-increases' ||
    (typeof inflationOption === 'string' && inflationOption.toLowerCase().includes('no bio'))
  ) {
    return 0;
  }
  if (typeof inflationOption === 'number') {
    return inflationOption;
  }
  if (typeof inflationOption === 'string') {
    const match = inflationOption.match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  }
  return 0;
}

function inflationOptionQualifiesForRange(
  inflationPercent: number,
  minInflationPercent: number,
  maxInflationPercent: number,
  options?: {
    compoundRequired?: boolean;
    isCompoundInflation?: boolean;
    bioPercent?: string | number | null;
  },
): boolean {
  if (options?.compoundRequired) {
    if (options.isCompoundInflation === false) {
      return false;
    }
    if (options.isCompoundInflation == null && typeof options.bioPercent === 'string') {
      const inferredCompound = options.bioPercent.toLowerCase().includes('compound');
      if (!inferredCompound) {
        return false;
      }
    }
  }
  return inflationPercent >= minInflationPercent && inflationPercent <= maxInflationPercent;
}



export interface IsPartnershipEligibleParams {
  state: string;
  ageAtIssue: number;
  bioPercent: string | number | null;
  policyType: number | null;
  isCompoundInflation?: boolean;
}

/**
 * Determines if a policy qualifies for Partnership based on state, age at issue, and inflation option.
 * Used for Care Assurance (Genworth) take-home packet routing and shop quote disclaimers.
 *
 * @returns true if Partnership-eligible, false otherwise
 */
export function isPartnershipEligible({
  state,
  ageAtIssue,
  bioPercent,
  policyType,
  isCompoundInflation,
}: IsPartnershipEligibleParams): boolean {
  try {
    if (policyType !== FundingPolicyType.careScoutCareAssurance) {
      return false;
    }

    const stateKey = state?.toUpperCase?.() ?? '';
    const config = partnershipStateConfig[stateKey];

    if (!config) {
      return false;
    }

    if (ageAtIssue >= AGE_NO_INFLATION_REQUIRED) {
      return true;
    }

    const inflationPercent = parseInflationPercentFromOption(bioPercent);
    if (inflationPercent === 0) {
      return false;
    }

    for (const band of config) {
      if (ageAtIssue >= band.age.min && ageAtIssue <= band.age.max) {
        return inflationOptionQualifiesForRange(
          inflationPercent,
          band.inflations.min,
          band.inflations.max,
          { compoundRequired: band.compoundRequired, isCompoundInflation, bioPercent },
        );
      }
    }
    return false;
  } catch (error) {
    // Using console.error to avoid circular dependency with @shared/log
    console.error('isPartnershipEligible error:', String(error), {
      state,
      ageAtIssue,
      bioPercent,
    });
    return false;
  }
}
